/**
 * DonationModal — top-up donation flow (D3). User picks a preset or types
 * a custom amount, hits Donate, redirects to Stripe Checkout. All funds
 * route to IronFire account pre-501(c)(3); allocation is tracked in DB
 * for the future Donors Wall + restricted-fund reporting.
 *
 * Surfaces from: Pricing page top-up strip, Impact page (D6 future).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { openStripeCheckout } from '../lib/stripeCheckout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Heart, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Selected row — avoid bg-enf-accent/15; CSS-var opacity breaks in Athlete mode (white-on-white). */
const presetButtonClass = (active) =>
  active
    ? 'bg-enf-surface-elevated border-enf-accent-fg ring-1 ring-enf-accent-fg/40'
    : 'bg-enf-surface-mid border-enf-surface-border hover:border-enf-accent-fg/30';

const DonationModal = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [presets, setPresets] = useState([]);
  const [custom, setCustom] = useState({ min: 1, max: 1000 });
  const [selected, setSelected] = useState('research');
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/payments/donation-options`)
      .then((res) => {
        setPresets(res.data?.presets || []);
        setCustom(res.data?.custom || { min: 1, max: 1000 });
      })
      .catch(() => {});
  }, [open]);

  const donate = async () => {
    if (!user) {
      window.location.href = '/login?return=/pricing';
      return;
    }
    setError('');
    const allocation = selected;
    let amount = null;
    if (allocation === 'custom') {
      const n = Number(customAmount);
      if (!n || n < custom.min || n > custom.max) {
        setError(`Custom amount must be between $${custom.min.toFixed(2)} and $${custom.max.toFixed(2)}.`);
        return;
      }
      amount = n;
    }
    setLoading(true);
    try {
      const body = { allocation, origin_url: window.location.origin };
      if (amount != null) body.amount = amount;
      console.log('[donation] POST /api/payments/checkout/donation', body);
      const res = await axios.post(`${API}/payments/checkout/donation`, body);
      console.log('[donation] response', res.data);
      if (res.data?.url) {
        await openStripeCheckout(res.data.url);
      } else {
        setError('Stripe did not return a checkout URL. Try again.');
        setLoading(false);
      }
    } catch (e) {
      console.error('[donation] checkout failed', e?.response?.status, e?.response?.data, e);
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      setError(detail || (status === 401 ? 'Please sign in again.' : `Donation checkout failed (${status || 'network'}).`));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="donation-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-enf-accent-fg" /> Add a contribution to IronLimb
          </DialogTitle>
          <DialogDescription>
            100% of your contribution routes to the allocation you pick. Not yet tax-deductible — IronLimb Foundation's 501(c)(3) is pending. We'll email you a receipt either way.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelected(p.id)}
              data-testid={`donation-preset-${p.id}`}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${presetButtonClass(selected === p.id)}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-enf-text-primary">{p.label}</span>
                <span className="text-sm font-mono text-enf-accent-fg">${p.amount.toFixed(2)}</span>
              </div>
            </button>
          ))}

          <button
            type="button"
            onClick={() => setSelected('custom')}
            data-testid="donation-preset-custom"
            className={`w-full text-left p-3 rounded-lg border transition-colors ${presetButtonClass(selected === 'custom')}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-enf-text-primary">Custom amount</span>
              <span className="text-xs text-enf-text-muted">${custom.min.toFixed(2)} – ${custom.max.toFixed(2)}</span>
            </div>
            {selected === 'custom' && (
              <div className="flex items-center gap-2">
                <span className="text-enf-text-muted text-sm">$</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={custom.min}
                  max={custom.max}
                  step="1"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Amount"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="donation-custom-amount"
                  className="w-32 bg-enf-surface-base border border-enf-surface-border rounded-md p-2 text-sm text-enf-text-primary"
                />
              </div>
            )}
          </button>

          {error && <div className="text-xs text-red-400">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 rounded-md text-sm border border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={donate}
              disabled={loading}
              data-testid="donation-submit-button"
              className="px-4 py-2 rounded-md text-sm font-semibold bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary disabled:opacity-60 flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
              Donate
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DonationModal;
